/**
 * Map boundary management — ONE component, two surfaces.
 *
 * Choropleth shapes are generated per country (Natural Earth → the India
 * point-of-view layer built by services/geo_boundaries.py), so an instance
 * starts with none and downloads the ones it needs.
 *
 *  - `<MapBoundaryStatus>` — the Visual Designer's Map options panel: status
 *    for the ONE country this visual needs, with a Download button when it is
 *    missing.
 *  - `<MapBoundariesManager>` — the Instance Admin console: the full country
 *    list with search, stored badges, and download / re-download.
 *
 * Both share `useMapBoundaries`, so the status shape, the polling and the
 * error handling can never drift between the two.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Download, Globe, RefreshCw, Search } from 'lucide-react'
import {
  downloadGeoCountry, fetchGeoJob, fetchGeoStatus,
  type GeoJob, type GeoStatus,
} from '@/api/geo'

const POLL_MS = 2000

export function useMapBoundaries() {
  const [status, setStatus] = useState<GeoStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [jobs, setJobs] = useState<Record<string, GeoJob>>({})
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const refresh = useCallback(async () => {
    try {
      const s = await fetchGeoStatus()
      setStatus(s)
      setError(null)
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail ?? (e as Error).message
      setError(msg || 'Could not read the boundary store')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    const t = timers.current
    return () => { Object.values(t).forEach(clearTimeout) }
  }, [refresh])

  const poll = useCallback((code: string) => {
    const tick = async () => {
      try {
        const job = await fetchGeoJob(code)
        setJobs((j) => ({ ...j, [code]: job }))
        if (job.status === 'running') {
          timers.current[code] = setTimeout(tick, POLL_MS)
          return
        }
      } catch (e) {
        setJobs((j) => ({
          ...j,
          [code]: { country: code, status: 'error', error: (e as Error).message },
        }))
      }
      refresh()
    }
    timers.current[code] = setTimeout(tick, POLL_MS)
  }, [refresh])

  const download = useCallback(async (code: string, force = false) => {
    setJobs((j) => ({ ...j, [code]: { country: code, status: 'running' } }))
    try {
      const job = await downloadGeoCountry(code, force)
      setJobs((j) => ({ ...j, [code]: job }))
      if (job.status === 'running') poll(code)
      else refresh()
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail ?? (e as Error).message
      setJobs((j) => ({ ...j, [code]: { country: code, status: 'error', error: msg } }))
    }
  }, [poll, refresh])

  return { status, loading, error, jobs, download, refresh }
}

const muted = { fontSize: 12, color: 'var(--color-text-secondary)' } as const

/** The app's standard inline spinner, sized for a button/caption row. */
function Spinner() {
  return <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
}

function JobLine({ job }: { job: GeoJob | undefined }) {
  if (!job) return null
  if (job.status === 'running') {
    const last = job.messages?.[job.messages.length - 1]
    return (
      <div style={{ ...muted, display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
        <Spinner />
        {last || 'Downloading boundaries…'}
        <span style={{ opacity: 0.7 }}>(the first download fetches ~52 MB of source data)</span>
      </div>
    )
  }
  if (job.status === 'error') {
    return <div style={{ ...muted, color: 'var(--color-danger, #d33)', marginTop: 6 }}>{job.error}</div>
  }
  return null
}

/** The Visual Designer panel: is THIS visual's country available? */
export function MapBoundaryStatus({ level, country }: { level: string; country: string }) {
  const { status, loading, error, jobs, download } = useMapBoundaries()
  const wantsCountryLevel = (level || 'auto').toLowerCase() === 'country'
  const code = (country || '').trim().toUpperCase()
  const target = wantsCountryLevel ? 'world' : code

  if (loading) return <div style={muted}>Checking map boundaries…</div>
  if (error) return <div style={{ ...muted, color: 'var(--color-danger, #d33)' }}>{error}</div>
  if (!status) return null

  const stored = target === 'world' ? status.world : (!!code && status.stored_codes.includes(code))
  const name = status.available.find((c) => c.code === code)?.name || code
  const job = jobs[target]

  return (
    <div style={{
      padding: '11px 13px', border: '1px solid var(--color-border)', borderRadius: 12,
      background: 'var(--color-bg-secondary)', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Globe size={15} style={{ opacity: 0.7 }} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>Map boundaries</span>
        {!target && (
          <span style={muted}>
            Set the Country option (e.g. IN) for state-level regions, or leave it
            blank and the map will detect the country from your data.
          </span>
        )}
        {target === 'world' && (
          <span style={muted}>Country outlines {status.world ? 'are downloaded' : 'are not downloaded yet'}.</span>
        )}
        {target && target !== 'world' && (
          <span style={muted}>
            {stored ? `${name} (${code}) is downloaded.` : `${name || code} is not downloaded on this server yet.`}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {target && !stored && (
          <button
            className="btn btn-secondary btn-sm"
            disabled={job?.status === 'running' || !status.can_build}
            onClick={() => download(target)}
            title={status.can_build ? '' : 'This server cannot build boundaries (shapely is not installed)'}
          >
            {job?.status === 'running'
              ? <><Spinner /> Downloading…</>
              : <><Download size={13} /> Download {target === 'world' ? 'base map' : code}</>}
          </button>
        )}
        {target && stored && (
          <span style={{ ...muted, display: 'flex', alignItems: 'center', gap: 4, color: 'var(--color-success, #1a7f37)' }}>
            <Check size={13} /> Ready
          </span>
        )}
      </div>
      <JobLine job={job} />
      {!status.can_build && !stored && (
        <div style={{ ...muted, marginTop: 6 }}>
          This server has no boundary builder installed — an instance admin must
          run <code>pip install shapely</code> or copy the files across.
        </div>
      )}
    </div>
  )
}

/** The Instance Admin section: every country, stored state, download/rebuild. */
export function MapBoundariesManager() {
  const { status, loading, error, jobs, download, refresh } = useMapBoundaries()
  const [query, setQuery] = useState('')
  const [onlyStored, setOnlyStored] = useState(false)

  if (loading) return <div style={muted}>Loading map boundaries…</div>
  if (error) return <div style={{ ...muted, color: 'var(--color-danger, #d33)' }}>{error}</div>
  if (!status) return null

  const q = query.trim().toLowerCase()
  const list = status.available.filter((c) => {
    if (onlyStored && !c.present) return false
    if (!q) return true
    return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
  })
  const totalBytes = status.stored.reduce((s, c) => s + (c.size ?? 0), 0)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Map boundaries</div>
          <div style={muted}>
            Region shapes for choropleth maps (Natural Earth 1:10m, public domain,
            India point-of-view). Stored at <code>{status.geo_dir}</code>.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={refresh}><RefreshCw size={13} /> Refresh</button>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, ...muted }}>
        <span>{status.stored.length} of {status.available.length || '—'} countries stored</span>
        <span>{(totalBytes / 1024 / 1024).toFixed(1)} MB on disk</span>
        <span>{status.state_aliases.toLocaleString()} region aliases indexed</span>
        <span>Base map: {status.world ? 'downloaded' : 'not downloaded'}</span>
        {!status.can_build && (
          <span style={{ color: 'var(--color-danger, #d33)' }}>
            builder unavailable (shapely not installed)
          </span>
        )}
      </div>

      {!status.world && (
        <div style={{
          padding: '11px 13px', border: '1px solid var(--color-border)', borderRadius: 12,
          background: 'var(--color-bg-secondary)', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <Globe size={15} style={{ opacity: 0.7 }} />
          <span style={{ fontSize: 13 }}>
            No boundaries downloaded yet. Start with the base map — it carries the
            country outlines every country-level choropleth draws, and the
            country picker below fills in once it lands.
          </span>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-primary btn-sm"
            disabled={jobs.world?.status === 'running' || !status.can_build}
            onClick={() => download('world')}
          >
            {jobs.world?.status === 'running'
              ? <><Spinner /> Downloading…</>
              : <><Download size={13} /> Download base map</>}
          </button>
          <div style={{ width: '100%' }}><JobLine job={jobs.world} /></div>
        </div>
      )}

      {status.available.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
              <Search size={14} style={{ position: 'absolute', left: 9, top: 9, opacity: 0.5 }} />
              <input
                className="input"
                style={{ paddingLeft: 28, width: '100%' }}
                placeholder="Search countries…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyStored} onChange={(e) => setOnlyStored(e.target.checked)} />
              Stored only
            </label>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 8, maxHeight: 420, overflowY: 'auto',
          }}>
            {list.map((c) => {
              const job = jobs[c.code]
              const busy = job?.status === 'running'
              return (
                <div key={c.code} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                  border: '1px solid var(--color-border)', borderRadius: 10,
                  background: c.present ? 'var(--color-bg-secondary)' : 'transparent',
                }}>
                  <span style={{ fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.name} <span style={{ opacity: 0.55 }}>{c.code}</span>
                  </span>
                  <div style={{ flex: 1 }} />
                  {job?.status === 'error' ? (
                    <span title={job.error ?? ''} style={{ fontSize: 11, color: 'var(--color-danger, #d33)' }}>failed</span>
                  ) : null}
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={busy || !status.can_build}
                    onClick={() => download(c.code, !!c.present)}
                    title={c.present ? 'Re-download (instance admin)' : 'Download boundaries'}
                  >
                    {busy ? <Spinner />
                      : c.present ? <RefreshCw size={13} /> : <Download size={13} />}
                  </button>
                </div>
              )
            })}
            {list.length === 0 && <div style={muted}>No countries match “{query}”.</div>}
          </div>
        </>
      )}
    </div>
  )
}
